/**
 * RUN 84 - one extension press, from `Confirm Push` to the answer, and its recovery when the page
 * reloads or the port drops (`12-tms-push-extension.md` sections 4, 5 and 6).
 *
 * The order of events, each a step the dialogue says out loud:
 *
 *   1. DISCOVER  `hello` again, at the press - given three seconds, since a worker Chrome has let
 *                sleep takes a moment to wake. No answer: nothing is prepared (section 4, step 1).
 *   2. PREPARE   a fresh press id (`crypto.randomUUID`), `POST /appointments/push/prepare`. The
 *                server gates, records the press and holds the day; a refusal records nothing. An
 *                answer that was LOST - no answer, or a server failure after which the press may
 *                have been recorded - is asked for once more with the SAME press id: the server
 *                answers a press it already holds, while it is open, with the plan it recorded, so
 *                the second ask prepares nothing new and a first that landed is not left holding
 *                the day for ten minutes.
 *   3. PUSH      the plan goes to the extension over a port, with its `holdRemainingMs` reduced by
 *                the time the page itself held it. The TD confirms or cancels in the extension's own
 *                window; progress comes back, then one result.
 *   4. REPORT    the result goes to `POST /appointments/push/report` EXACTLY AS THE EXTENSION SENT
 *                IT. The API validates it and judges it against its own plan.
 *   5. ACK       once the API has RECORDED the report (its genuine answer, a replay included), the
 *                page tells the extension `ack`, and the extension drops its copy (section 6b).
 *                Never before: until then the extension's copy is what survives a reload.
 *
 * **Release - only when the plan certainly never reached the extension** (section 6b). The
 * extension answered `refused` for `busy` or `invalid-message` (it did not take the plan), or it
 * was absent after all (no runtime, no real id, a port that could not be opened or written to, or
 * this page's own one-push-at-a-time guard): the page asks `POST /appointments/push/release`, which
 * records every owed match "not sent" and frees the day at once rather than in ten minutes. A port
 * that closed, an `internal` refusal, an `already-finished` one (the extension HAS run that press),
 * or an extension that holds nothing when asked is NOT such a case - the extension may have posted,
 * and a release would record "nothing was sent" over a push that may have landed - so those are
 * asked about (`status`), and a press nobody can account for keeps its hold until it lapses. Before
 * saying so, the page asks the API whether the press is already recorded (another tab, or a report
 * before a reload), and says what it holds.
 *
 * **The hold is a DURATION on this page, never an instant compared with the laptop's clock**
 * (contract change, 18 Sep 2026). The prepare answers `holdRemainingMs` - the hold's remaining time
 * on the SERVER's clock - and the page counts that down on its own steady clock
 * (`performance.timeOrigin + performance.now()`), so a laptop clock set wrong cannot make a live
 * hold look lapsed or a lapsed one look live. `holdUntil` is only ever PRINTED, on the tournament's
 * clock.
 *
 * A press id is remembered for the tab (`sessionStorage`) from the moment it is minted until the
 * press has a genuine answer or a definite refusal, so neither a reload nor a report that failed
 * part-way can lose it: on the next load the page asks the extension `status` and reports what it
 * kept (section 4, step 6), and a report is RESUMABLE - the same result sent again writes only the
 * receipts that are missing. The same recovery runs at once when the port closes with no result.
 *
 * **Nothing here invents a result.** The only result ever reported is one the extension sent and
 * this page could read, and it is reported as it came.
 *
 * **The day push** (section 6c; the TD's Decision #5 = B, 19 Sep 2026) - `runTmsDayPush`, at the end
 * of this file - is the same press for every competition of the day at once, and forks none of the
 * above: `hello`, then a fresh press id per competition, remembered for the tab BEFORE anything is
 * asked; `POST /appointments/push/prepare-day` (a lost answer asked once more with the SAME press
 * ids); the plans in ONE `push-day` message on one port; and each result, as it arrives and in plan
 * order, through THIS file's `reportTmsPush` - the unchanged report, and `ack` only after its genuine
 * answer. Every press of a day push is an ordinary press here too: released by `releaseTmsPush` when
 * the day push's message certainly never reached the extension, asked about by `recoverTmsPush` when
 * the port was lost, each with the memory of its own press id alone.
 */

import type { TmsCompetitionId } from "@hockeytd/shared";

import { ApiError } from "../../api/client.js";
import type {
  AppointmentPushReceiptBody,
  DayPushMatchRefBody,
  DayPushReceiptsBody,
  ExtensionPushDayPlanBody,
  ExtensionPushLeftOutBody,
  ExtensionPushPlanBody,
  ExtensionPushReleaseBody,
  ExtensionPushReportBody,
  ExtensionPushResultMessage,
} from "../../api/types.js";
import {
  TMS_PUSH_PRESS_HELLO_TIMEOUT_MS,
  type TmsPushBridge,
  type TmsPushDayPortEnding,
  type TmsPushPortEnding,
  type TmsPushRefusalReason,
  type TmsPushStage,
  type TmsPushStatus,
} from "./tmsPushExtension.js";

/* -- The press, as this tab remembers it ------------------------------------------------------- */

/** One press, as far as this tab needs to remember it across a reload. */
export interface RememberedPress {
  readonly pressId: string;
  readonly day: string;
  readonly competitionId: TmsCompetitionId;
  /** The hold's end as the SERVER gave it - for printing on the tournament's clock, and only that. */
  readonly holdUntil: string | null;
  /**
   * The hold's end on THIS DEVICE's steady clock (epoch milliseconds): the moment the prepare answer
   * arrived plus its `holdRemainingMs`. Every decision about the hold is made against this. Null
   * until the prepare has answered - a reload before then left nothing with the extension.
   */
  readonly holdDeadline: number | null;
  /** The matches the plan owes, in send order, once the prepare has answered. */
  readonly planned: readonly DayPushMatchRefBody[] | null;
}

/** Where the remembered press lives. One per tab. */
export interface TmsPushMemory {
  read(): RememberedPress | null;
  write(press: RememberedPress): void;
  clear(): void;
}

/** The `sessionStorage` key. Per tab by the storage's own nature (section 4, step 6). */
export const TMS_PUSH_MEMORY_KEY = "hockeytd.tmsPush.press";

const PRESS_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const DAY = /^\d{4}-\d{2}-\d{2}$/;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * A remembered press read back from storage, or null. Storage is input like any other - another
 * build of this page, or a hand in the developer tools, may have written it - so it is read field
 * by field and anything else is dropped.
 */
export function readRememberedPress(raw: unknown): RememberedPress | null {
  if (!isRecord(raw)) return null;
  const pressId = raw["pressId"];
  const day = raw["day"];
  const competitionId = raw["competitionId"];
  const holdUntil = raw["holdUntil"];
  const holdDeadline = raw["holdDeadline"];
  const planned = raw["planned"];
  if (typeof pressId !== "string" || !PRESS_ID.test(pressId)) return null;
  if (typeof day !== "string" || !DAY.test(day)) return null;
  if (competitionId !== "1916" && competitionId !== "1917") return null;
  if (holdUntil !== null && (typeof holdUntil !== "string" || Number.isNaN(Date.parse(holdUntil)))) {
    return null;
  }
  if (holdDeadline !== null && (typeof holdDeadline !== "number" || !Number.isFinite(holdDeadline))) {
    return null;
  }
  let matches: DayPushMatchRefBody[] | null = null;
  if (planned !== null) {
    if (!Array.isArray(planned)) return null;
    matches = [];
    for (const entry of planned as unknown[]) {
      if (!isRecord(entry)) return null;
      const id = entry["tmsMatchDbId"];
      const name = entry["displayMatchNo"];
      if (typeof id !== "number" || !Number.isSafeInteger(id) || typeof name !== "string") return null;
      matches.push({ tmsMatchDbId: id, displayMatchNo: name });
    }
  }
  return { pressId, day, competitionId, holdUntil, holdDeadline, planned: matches };
}

/** The tab's `sessionStorage`, or null where there is none a page can use. */
function tabStorage(): Storage | null {
  try {
    const storage = globalThis.sessionStorage as Storage | undefined;
    if (storage === undefined || storage === null) return null;
    if (typeof storage.getItem !== "function" || typeof storage.setItem !== "function") return null;
    return storage;
  } catch {
    return null;
  }
}

/**
 * The live memory: `sessionStorage`, guarded. A storage that throws (a private window, a quota)
 * forgets rather than breaks the push - the push itself does not depend on being remembered.
 */
export const SESSION_TMS_PUSH_MEMORY: TmsPushMemory = {
  read(): RememberedPress | null {
    const storage = tabStorage();
    if (storage === null) return null;
    try {
      const text = storage.getItem(TMS_PUSH_MEMORY_KEY);
      if (text === null) return null;
      const press = readRememberedPress(JSON.parse(text) as unknown);
      if (press === null) storage.removeItem(TMS_PUSH_MEMORY_KEY);
      return press;
    } catch {
      return null;
    }
  },
  write(press: RememberedPress): void {
    try {
      tabStorage()?.setItem(TMS_PUSH_MEMORY_KEY, JSON.stringify(press));
    } catch {
      /* Not remembered. The press goes on. */
    }
  },
  clear(): void {
    try {
      tabStorage()?.removeItem(TMS_PUSH_MEMORY_KEY);
    } catch {
      /* Nothing to clear. */
    }
  },
};

/**
 * The device's steady clock, as epoch milliseconds: the page's time origin plus the monotonic time
 * since. Within a page it never jumps with the wall clock; across a reload it continues on the
 * device's own reckoning - which is all a DURATION counted on this device needs.
 */
export function steadyClock(): number {
  if (typeof performance !== "undefined" && typeof performance.now === "function") {
    return performance.timeOrigin + performance.now();
  }
  return Date.now();
}

/* -- The steps, and how a press ends -------------------------------------------------------------- */

/** Where a press is, as the dialogue says it. The four middle ones are the extension's own stages. */
export type TmsPushStep =
  | "discovering"
  | "preparing"
  | "handing-over"
  | TmsPushStage
  | "reporting"
  | "releasing"
  | "recovering"
  | "waiting";

/**
 * How the page KNOWS a prepared plan never reached the extension - the only grounds for a release.
 * `by: "extension"`: its own `refused` answer, in its own words - `busy` or `invalid-message` only;
 * `already-finished` means it HAS run that press, and `internal` that it may hold the plan, so
 * neither is ever released. `by: "page"`: `absent` - no runtime, no real id, or a port that could
 * not be opened or written to; `busy` - this page's own push was still out, so this one was never
 * sent. *Day push (section 6c):* `too-large` - the `push-day` message would have been longer than the
 * contract's 1 MiB, so the page never sent it. *Fix round 1:* `gone` - the page was closed after the
 * prepare answered and before it handed anything over, so the plan never left it.
 */
export type TmsPushNotTaken =
  | {
      readonly by: "extension";
      readonly reason: Exclude<TmsPushRefusalReason, "internal" | "already-finished">;
      readonly detail: string;
    }
  | { readonly by: "page"; readonly reason: "absent" | "busy" | "too-large" | "gone" };

/** How a press ended. */
export type TmsPushEnding =
  /** No extension answered at the press: nothing was prepared, and nothing held. */
  | { readonly kind: "absent" }
  /** The prepare was refused, or never answered; the error says which. Nothing went to TMS. */
  | { readonly kind: "refused"; readonly error: unknown }
  /** The extension's result, and the API's genuine answer to it. */
  | {
      readonly kind: "reported";
      readonly press: RememberedPress;
      readonly result: ExtensionPushResultMessage;
      readonly answer: ExtensionPushReportBody;
    }
  /**
   * A result the report did not record. `retryable`: the press is still remembered and the same
   * result may be sent again - the report is resumable - after the bounded tries have run out.
   */
  | {
      readonly kind: "report-refused";
      readonly press: RememberedPress;
      readonly result: ExtensionPushResultMessage;
      readonly error: unknown;
      readonly retryable: boolean;
    }
  /** No result the page can report. */
  | {
      readonly kind: "no-result";
      readonly press: RememberedPress;
      /**
       * `unknown` - the extension holds nothing for this press; `unreadable` - it answered in a shape
       * the page cannot read; `no-answer` - it did not answer; `lapsed` - the hold lapsed while it
       * was still working. *Day push (section 6c, fix round 1):* `failed` - this page itself failed
       * while handling the press, so it does not know what became of it; the press stays remembered
       * wherever it was, and is asked about again when the page next opens.
       */
      readonly why: "unknown" | "unreadable" | "no-answer" | "lapsed" | "failed";
      /**
       * What the API already holds for this press - its receipts, when it was reported (by another
       * tab, or before a reload) - or null when there are none, or they could not be asked for.
       */
      readonly recorded: readonly AppointmentPushReceiptBody[] | null;
    }
  /**
   * The plan never reached the extension ({@link TmsPushNotTaken}), and the press was released:
   * every owed match is recorded "not sent", and the press no longer holds the day - which is then
   * free, for a single press; a press of a day push frees only its own hold, and the day push's
   * other presses may still hold the day (fix round 2, R5).
   */
  | {
      readonly kind: "released";
      readonly press: RememberedPress;
      readonly notTaken: TmsPushNotTaken;
      readonly answer: ExtensionPushReleaseBody;
    }
  /** The plan never reached the extension, and the release was not recorded: the hold runs out. */
  | {
      readonly kind: "release-refused";
      readonly press: RememberedPress;
      readonly notTaken: TmsPushNotTaken;
      readonly error: unknown;
    }
  /** The page went away while waiting; the press is still remembered. */
  | { readonly kind: "stopped" };

export interface TmsPushTiming {
  /** How often `status` is asked again while the extension says the push is still running. */
  readonly pollMs: number;
  /**
   * How long after the hold's end the page stops waiting. The extension posts nothing once two
   * minutes of the hold are left (section 6a), and a post begun before that is over well inside it.
   */
  readonly graceMs: number;
  /** How many times one report is sent before the TD is told to reload and try again. */
  readonly reportAttempts: number;
  /** The pause between two of those tries - and before the one second prepare of a lost answer. */
  readonly reportRetryMs: number;
}

export const DEFAULT_TMS_PUSH_TIMING: TmsPushTiming = {
  pollMs: 2_000,
  graceMs: 30_000,
  reportAttempts: 3,
  reportRetryMs: 1_500,
};

export interface TmsPushFlowDeps {
  readonly bridge: TmsPushBridge;
  readonly prepare: (body: {
    day: string;
    competitionId: TmsCompetitionId;
    pressId: string;
  }) => Promise<ExtensionPushPlanBody>;
  readonly report: (body: {
    pressId: string;
    result: ExtensionPushResultMessage;
  }) => Promise<ExtensionPushReportBody>;
  /** `POST /appointments/push/release` - asked ONLY when the plan never reached the extension. */
  readonly release: (body: { pressId: string }) => Promise<ExtensionPushReleaseBody>;
  /**
   * `GET /appointments/push-receipts?day=` - asked before the page says it has no result, in case
   * the press is already recorded. Optional: without it, the page says only what the extension said.
   */
  readonly receipts?: (day: string) => Promise<DayPushReceiptsBody>;
  readonly memory: TmsPushMemory;
  readonly mintPressId: () => string;
  /** The device's steady clock ({@link steadyClock}). Never compared with a server instant. */
  readonly clock: () => number;
  readonly sleep: (ms: number) => Promise<void>;
  readonly timing: TmsPushTiming;
  /**
   * Every step, as it is reached, with the press once there is one - and, while a report is being
   * tried again, which try this is.
   */
  readonly onStep: (step: TmsPushStep, press: RememberedPress | null, attempt?: number) => void;
  /** True once the page is gone: a recovery or a report that is only waiting stops. */
  readonly gone: () => boolean;
}

/**
 * What reporting, releasing and recovering ONE press need - every dependency but the prepare. The day
 * push (section 6c) runs each of its presses through these very functions, with the memory of that one
 * press ({@link dayPressMemory}); the single press passes its whole {@link TmsPushFlowDeps}.
 */
export type TmsPushPressDeps = Omit<TmsPushFlowDeps, "prepare">;

/** An answer to the report that arrived as a success and is not a press answer. */
export class TmsPushAnswerUnreadableError extends Error {
  public constructor() {
    super("The report was answered, but not with this push's answer.");
    this.name = "TmsPushAnswerUnreadableError";
  }
}

/**
 * Whether a refused report leaves the press to be reported again: no answer at all, a server
 * failure (the report is resumable - a second send writes only the missing receipts), the same
 * press's report being recorded at that moment, a lapsed sign-in, or a success that was not a press
 * answer. Anything else is a definite refusal, and the press is forgotten.
 */
export function isRetryableReportRefusal(error: unknown): boolean {
  if (!(error instanceof ApiError)) return true;
  if (error.status === 0 || error.status === 401 || error.status >= 500) return true;
  return error.code === "push_in_progress";
}

/**
 * Whether the hold has lapsed - on the device's steady clock, against the deadline the server's
 * remaining time gave it, with `graceMs` after it. A press with no deadline never held the day.
 */
export function holdLapsed(press: RememberedPress, clock: number, graceMs: number): boolean {
  if (press.holdDeadline === null) return true;
  return clock > press.holdDeadline + graceMs;
}

/** Why a plan cannot be handed on, or null when it is the press the page asked for. */
function planFault(plan: ExtensionPushPlanBody, press: RememberedPress): string | null {
  if (!isRecord(plan)) return "the server's answer is not a plan";
  if (typeof plan.pressId !== "string" || plan.pressId.toLowerCase() !== press.pressId) {
    return "the plan is for another push";
  }
  if (plan.day !== press.day || plan.competitionId !== press.competitionId) {
    return "the plan is for another day or competition";
  }
  if (!Array.isArray(plan.payloads) || plan.payloads.length === 0) {
    return "the plan owes no match";
  }
  if (typeof plan.holdUntil !== "string" || Number.isNaN(Date.parse(plan.holdUntil))) {
    return "the plan does not say when its hold ends";
  }
  const remaining = (plan as { holdRemainingMs?: unknown }).holdRemainingMs;
  if (typeof remaining !== "number" || !Number.isSafeInteger(remaining) || remaining < 0) {
    return "the plan does not say how long its hold has left";
  }
  return null;
}

/** A plan the page will not hand to the extension. Nothing went to the extension or to TMS. */
export class TmsPushPlanMismatchError extends Error {
  public constructor(why: string) {
    super(`The server's plan could not be handed on: ${why}.`);
    this.name = "TmsPushPlanMismatchError";
  }
}

function isRefList(value: unknown): boolean {
  return (
    Array.isArray(value) &&
    value.every(
      (entry) =>
        isRecord(entry) &&
        typeof entry["tmsMatchDbId"] === "number" &&
        typeof entry["displayMatchNo"] === "string",
    )
  );
}

/**
 * Whether a report's 2xx body is this press's answer (section A9a's shape, beside the day). Only
 * such an answer ends a press and lets the tab forget it; anything else is tried again.
 */
export function isPressAnswer(body: unknown, pressId: string): body is ExtensionPushReportBody {
  if (!isRecord(body)) return false;
  if (typeof body["pressId"] !== "string" || body["pressId"].toLowerCase() !== pressId) return false;
  if (!["sent", "partial", "failed"].includes(String(body["outcome"]))) return false;
  if (typeof body["replayed"] !== "boolean" || typeof body["day"] !== "string") return false;
  const matches = body["matches"];
  if (!Array.isArray(matches)) return false;
  for (const match of matches as unknown[]) {
    if (!isRecord(match) || typeof match["tmsMatchDbId"] !== "number") return false;
    if (typeof match["displayMatchNo"] !== "string" || typeof match["sent"] !== "boolean") return false;
    const receipt = match["receipt"];
    if (!isRecord(receipt) || typeof receipt["detail"] !== "string") return false;
    if (match["sent"] === true && typeof match["locked"] !== "boolean") return false;
    if (match["sent"] === false && typeof match["reason"] !== "string") return false;
  }
  const notAttempted = body["notAttempted"];
  if (
    !isRefList(notAttempted) ||
    !(notAttempted as unknown[]).every((entry) => isRecord(entry) && typeof entry["detail"] === "string")
  ) {
    return false;
  }
  if (!isRefList(body["alreadyOnTms"]) || !isRefList(body["refusedOfficial"])) return false;
  const collateral = body["collateral"];
  if (
    collateral !== undefined &&
    !(Array.isArray(collateral) && collateral.every((item) => typeof item === "string"))
  ) {
    return false;
  }
  const scope = body["scope"];
  return (
    isRecord(scope) &&
    Array.isArray(body["fixtures"]) &&
    Array.isArray(body["warnings"]) &&
    Array.isArray(body["workloads"]) &&
    isRecord(body["counts"])
  );
}

/**
 * REPORT - the result, exactly as it was read, to the API: the one place a result is reported.
 *
 * Tried up to `reportAttempts` times while the refusal leaves it to be tried again; the press is
 * forgotten only on a genuine answer or a definite refusal. When the tries run out it is still
 * remembered, and the TD is told the result is kept and to reload to try again.
 */
export async function reportTmsPush(
  deps: TmsPushPressDeps,
  press: RememberedPress,
  result: ExtensionPushResultMessage,
): Promise<TmsPushEnding> {
  const attempts = Math.max(1, deps.timing.reportAttempts);
  let lastError: unknown = null;
  for (let attempt = 1; attempt <= attempts; attempt += 1) {
    deps.onStep("reporting", press, attempt);
    try {
      const answer = await deps.report({ pressId: press.pressId, result });
      if (!isPressAnswer(answer, press.pressId)) throw new TmsPushAnswerUnreadableError();
      deps.memory.clear();
      // Recorded by the API: now, and only now, the extension may drop its copy (section 6b).
      acknowledge(deps, press.pressId);
      return { kind: "reported", press, result, answer };
    } catch (error) {
      lastError = error;
      if (!isRetryableReportRefusal(error)) {
        deps.memory.clear();
        return { kind: "report-refused", press, result, error, retryable: false };
      }
    }
    if (attempt < attempts) {
      await deps.sleep(deps.timing.reportRetryMs);
      if (deps.gone()) return { kind: "stopped" };
    }
  }
  return { kind: "report-refused", press, result, error: lastError, retryable: true };
}

/**
 * ACK - the API has recorded the press's report, so the extension may forget its copy. Sent and not
 * waited for: whatever becomes of it, what matters is already recorded, and an extension that missed
 * it keeps the result until it drops old ones itself.
 */
function acknowledge(deps: TmsPushPressDeps, pressId: string): void {
  try {
    void deps.bridge.ack(pressId).catch(() => undefined);
  } catch {
    /* Not acknowledged: the extension keeps its copy a while longer. Nothing is lost. */
  }
}

/**
 * RELEASE - the plan never reached the extension ({@link TmsPushNotTaken}): free the day now, with
 * every owed match recorded "not sent", rather than hold it for ten minutes. Asked once; the tab
 * forgets the press either way, because the extension holds nothing for it to recover.
 */
export async function releaseTmsPush(
  deps: TmsPushPressDeps,
  press: RememberedPress,
  notTaken: TmsPushNotTaken,
): Promise<TmsPushEnding> {
  deps.onStep("releasing", press);
  try {
    const answer = await deps.release({ pressId: press.pressId });
    if (!isPressAnswer(answer, press.pressId)) throw new TmsPushAnswerUnreadableError();
    deps.memory.clear();
    return { kind: "released", press, notTaken, answer };
  } catch (error) {
    deps.memory.clear();
    return { kind: "release-refused", press, notTaken, error };
  }
}

/**
 * RECOVER - ask the extension what it kept for a press, report a result it kept, and wait while it
 * says the push is still running (until the hold has lapsed). Run on load for a remembered press,
 * and at once when the port closes with no result.
 */
/**
 * What the API already holds for a press the extension cannot account for: its receipts, when it
 * was reported - by another tab, or before a reload - or null. Never throws; a failure to ask is null.
 */
async function recordedFor(deps: TmsPushPressDeps, press: RememberedPress): Promise<readonly AppointmentPushReceiptBody[] | null> {
  if (deps.receipts === undefined) return null;
  try {
    const day = await deps.receipts(press.day);
    const mine = day.receipts.filter((receipt) => receipt.pressId.toLowerCase() === press.pressId);
    return mine.length === 0 ? null : mine;
  } catch {
    return null;
  }
}

async function noResult(
  deps: TmsPushPressDeps,
  press: RememberedPress,
  why: Extract<TmsPushEnding, { kind: "no-result" }>["why"],
): Promise<TmsPushEnding> {
  return { kind: "no-result", press, why, recorded: await recordedFor(deps, press) };
}

export async function recoverTmsPush(
  deps: TmsPushPressDeps,
  press: RememberedPress,
): Promise<TmsPushEnding> {
  deps.onStep("recovering", press);
  const planned = press.planned?.map((match) => match.tmsMatchDbId) ?? null;
  for (;;) {
    let status: TmsPushStatus;
    try {
      status = await deps.bridge.status(press.pressId, planned);
    } catch {
      status = { kind: "no_answer" };
    }
    switch (status.kind) {
      case "result":
        return reportTmsPush(deps, press, status.result);
      case "unknown":
        deps.memory.clear();
        return noResult(deps, press, "unknown");
      case "unreadable":
        deps.memory.clear();
        return noResult(deps, press, "unreadable");
      case "no_answer":
        // Remembered while the day is still held, so the next load asks again; forgotten once the
        // hold has lapsed, when there is nothing left for the page to wait on.
        if (holdLapsed(press, deps.clock(), deps.timing.graceMs)) deps.memory.clear();
        return noResult(deps, press, "no-answer");
      case "pending":
        if (holdLapsed(press, deps.clock(), deps.timing.graceMs)) {
          // Past the hold and its margin the extension posts nothing (section 6a), so whatever it
          // still holds cannot have reached TMS: nothing is left to record, and the tab lets it go
          // rather than hold every later push back behind a window nobody closed.
          deps.memory.clear();
          return noResult(deps, press, "lapsed");
        }
        deps.onStep("waiting", press);
        await deps.sleep(deps.timing.pollMs);
        if (deps.gone()) return { kind: "stopped" };
        break;
    }
  }
}

/**
 * Whether a prepare's answer may have been LOST rather than refused: no answer at all, or a server
 * failure - after either, the server may have recorded the press and be holding the day.
 */
export function isLostPrepareAnswer(error: unknown): boolean {
  return !(error instanceof ApiError) || error.status === 0 || error.status >= 500;
}

/**
 * PREPARE - asked once, and once more with the SAME press id when its answer was lost
 * ({@link isLostPrepareAnswer}): the server answers a press id it already holds, while the press is
 * open, with the plan it recorded - so the second ask never prepares a second press, and one that
 * did land gets its plan to the extension instead of holding the day for ten minutes unused.
 */
async function prepareTmsPush(deps: TmsPushFlowDeps, press: RememberedPress): Promise<ExtensionPushPlanBody> {
  const body = { day: press.day, competitionId: press.competitionId, pressId: press.pressId };
  try {
    return await deps.prepare(body);
  } catch (error) {
    if (!isLostPrepareAnswer(error)) throw error;
    await deps.sleep(deps.timing.reportRetryMs);
    if (deps.gone()) throw error;
    return deps.prepare(body);
  }
}

/**
 * The press, from `Confirm Push`: discover, prepare, push, report - or recover when the port
 * closes first. Resolves with how it ended; never throws.
 */
export async function runTmsPush(
  deps: TmsPushFlowDeps,
  target: { readonly day: string; readonly competitionId: TmsCompetitionId },
): Promise<TmsPushEnding> {
  deps.onStep("discovering", null);
  let present: boolean;
  try {
    // Three seconds at the press: a worker Chrome has let sleep takes a moment to answer.
    present = (await deps.bridge.hello(TMS_PUSH_PRESS_HELLO_TIMEOUT_MS)).kind === "present";
  } catch {
    present = false;
  }
  if (!present) return { kind: "absent" };

  let press: RememberedPress = {
    pressId: deps.mintPressId().toLowerCase(),
    day: target.day,
    competitionId: target.competitionId,
    holdUntil: null,
    holdDeadline: null,
    planned: null,
  };
  // Remembered before the prepare is asked: a reload while it is out still knows the press id.
  deps.memory.write(press);
  deps.onStep("preparing", press);

  let plan: ExtensionPushPlanBody;
  try {
    plan = await prepareTmsPush(deps, press);
  } catch (error) {
    deps.memory.clear();
    return { kind: "refused", error };
  }
  // The hold starts counting down on this device the moment its remaining time arrived.
  const arrived = deps.clock();
  const fault = planFault(plan, press);
  if (fault !== null) {
    deps.memory.clear();
    return { kind: "refused", error: new TmsPushPlanMismatchError(fault) };
  }

  press = {
    ...press,
    holdUntil: plan.holdUntil,
    holdDeadline: arrived + plan.holdRemainingMs,
    planned: plan.payloads.map((payload) => ({
      tmsMatchDbId: payload.tmsMatchDbId,
      displayMatchNo: payload.displayMatchNo,
    })),
  };
  deps.memory.write(press);
  // Fix round 1 (day push review, P9): a page that has gone while the prepare was answered hands
  // nothing over - nobody is left to follow the push - and the plan certainly never left it, which
  // is 6b's ground for a release: the day is freed now rather than held for ten minutes.
  if (deps.gone()) {
    await releaseTmsPush(deps, press, { by: "page", reason: "gone" });
    return { kind: "stopped" };
  }
  deps.onStep("handing-over", press);

  // The plan as the server answered it, but for its remaining time, which is what is left of it NOW:
  // the extension counts its own deadline from that duration, never from a clock of its own.
  const handed: ExtensionPushPlanBody = {
    ...plan,
    holdRemainingMs: Math.max(0, plan.holdRemainingMs - Math.round(deps.clock() - arrived)),
  };

  // The page waits for the extension no longer than the hold, plus the margin - a duration on the
  // device's steady clock. Past it the extension posts nothing, and the page asks what it kept.
  const stop = new AbortController();
  const holdDeadline = press.holdDeadline ?? arrived;
  const timer = setTimeout(
    () => {
      stop.abort();
    },
    Math.max(0, holdDeadline + deps.timing.graceMs - deps.clock()),
  );
  let ending: TmsPushPortEnding;
  try {
    ending = await deps.bridge.push(
      handed,
      (stage) => {
        deps.onStep(stage, press);
      },
      stop.signal,
    );
  } catch {
    // The bridge answers every ending rather than throwing; a bridge that threw is read as a port
    // that closed with no result, which is what the page can honestly say it knows.
    ending = { kind: "lost" };
  } finally {
    clearTimeout(timer);
  }

  switch (ending.kind) {
    case "result":
      return reportTmsPush(deps, press, ending.result);
    case "unreadable":
      deps.memory.clear();
      return noResult(deps, press, "unreadable");
    case "refused":
      // `internal`: the extension failed while taking the plan, and may hold it. `already-finished`:
      // it HAS run this press - it may have posted. Either way: ask, never release.
      if (ending.reason === "internal" || ending.reason === "already-finished") return recoverTmsPush(deps, press);
      return releaseTmsPush(deps, press, { by: "extension", reason: ending.reason, detail: ending.detail });
    case "busy":
      return releaseTmsPush(deps, press, { by: "page", reason: "busy" });
    case "unavailable":
      return releaseTmsPush(deps, press, { by: "page", reason: "absent" });
    case "lost":
    case "stopped":
      return recoverTmsPush(deps, press);
  }
}

/* -- The day push (section 6c; Decision #5 = B, 19 Sep 2026) ------------------------------------ */

/**
 * The day push's presses, as this tab remembers them across a reload: the single press's
 * {@link RememberedPress}, one per competition of the day push, in the send order. Its own key, so
 * the single press's memory is exactly as it was.
 */
export interface TmsDayPushMemory {
  read(): readonly RememberedPress[];
  write(presses: readonly RememberedPress[]): void;
  clear(): void;
}

/** The day push's `sessionStorage` key. Per tab by the storage's own nature. */
export const TMS_PUSH_DAY_MEMORY_KEY = "hockeytd.tmsPush.day";

/** The most presses one day push carries (section 6c: 1-4). */
const DAY_PUSH_MAX_PRESSES = 4;

/**
 * A day push's remembered presses read back from storage, or null. Read as the single press is -
 * field by field, each entry through {@link readRememberedPress} - and as one day push: at most four,
 * one day, one press per competition, no press id twice. Anything else is not one, and is dropped
 * whole.
 */
export function readRememberedDay(raw: unknown): RememberedPress[] | null {
  if (!Array.isArray(raw) || raw.length === 0 || raw.length > DAY_PUSH_MAX_PRESSES) return null;
  const presses: RememberedPress[] = [];
  for (const entry of raw as unknown[]) {
    const press = readRememberedPress(entry);
    if (press === null) return null;
    presses.push(press);
  }
  const day = presses[0]?.day;
  if (presses.some((press) => press.day !== day)) return null;
  if (new Set(presses.map((press) => press.pressId.toLowerCase())).size !== presses.length) return null;
  if (new Set(presses.map((press) => press.competitionId)).size !== presses.length) return null;
  return presses;
}

/** The live day memory: `sessionStorage`, guarded exactly as the single press's is. */
export const SESSION_TMS_DAY_PUSH_MEMORY: TmsDayPushMemory = {
  read(): readonly RememberedPress[] {
    const storage = tabStorage();
    if (storage === null) return [];
    try {
      const text = storage.getItem(TMS_PUSH_DAY_MEMORY_KEY);
      if (text === null) return [];
      const presses = readRememberedDay(JSON.parse(text) as unknown);
      if (presses === null) storage.removeItem(TMS_PUSH_DAY_MEMORY_KEY);
      return presses ?? [];
    } catch {
      return [];
    }
  },
  write(presses: readonly RememberedPress[]): void {
    try {
      tabStorage()?.setItem(TMS_PUSH_DAY_MEMORY_KEY, JSON.stringify(presses));
    } catch {
      /* Not remembered. The day push goes on. */
    }
  },
  clear(): void {
    try {
      tabStorage()?.removeItem(TMS_PUSH_DAY_MEMORY_KEY);
    } catch {
      /* Nothing to clear. */
    }
  },
};

/**
 * ONE press of a day push, remembered on its own: a {@link TmsPushMemory} over the day's list, scoped
 * to `pressId`. `reportTmsPush`, `releaseTmsPush` and `recoverTmsPush` forget a press through it - and
 * forget that press alone, so a press recorded early lets go of its memory while the others keep
 * theirs. The last one forgotten clears the key.
 */
export function dayPressMemory(memory: TmsDayPushMemory, pressId: string): TmsPushMemory {
  return {
    read(): RememberedPress | null {
      return memory.read().find((press) => press.pressId === pressId) ?? null;
    },
    write(press: RememberedPress): void {
      const all = memory.read();
      const at = all.findIndex((entry) => entry.pressId === press.pressId);
      memory.write(at < 0 ? [...all, press] : all.map((entry, index) => (index === at ? press : entry)));
    },
    clear(): void {
      const rest = memory.read().filter((entry) => entry.pressId !== pressId);
      if (rest.length === 0) memory.clear();
      else memory.write(rest);
    },
  };
}

/** How ONE press ended - the endings a single press answers with, and a day push's presses too. */
export type TmsPushPressEnding = Extract<
  TmsPushEnding,
  | { kind: "reported" }
  | { kind: "report-refused" }
  | { kind: "no-result" }
  | { kind: "released" }
  | { kind: "release-refused" }
>;

function isPressEnding(ending: TmsPushEnding): ending is TmsPushPressEnding {
  return (
    ending.kind === "reported" ||
    ending.kind === "report-refused" ||
    ending.kind === "no-result" ||
    ending.kind === "released" ||
    ending.kind === "release-refused"
  );
}

/** One press of a day push and how it ended. */
export interface TmsDayPushPress {
  readonly press: RememberedPress;
  readonly ending: TmsPushPressEnding;
}

/** How a day push ended. */
export type TmsDayPushEnding =
  /** No extension answered at the press - or one that cannot carry a day push: nothing prepared. */
  | { readonly kind: "absent" }
  /** The prepare-day was refused, or never answered; the error says which. Nothing went to TMS. */
  | { readonly kind: "refused"; readonly error: unknown }
  /**
   * Every press of the day push has its ending, in the send order, and the competitions the server
   * left out of it, with the reason.
   */
  | {
      readonly kind: "done";
      readonly day: string;
      readonly presses: readonly TmsDayPushPress[];
      readonly leftOut: readonly ExtensionPushLeftOutBody[];
      /**
       * Fix round 1 (P1) - every answer the API gave this day push's reports and releases, IN THE
       * ORDER THEY ARRIVED, across `Report Again` too: each carries the whole day as it then stood,
       * so the LAST is the day as it stands now - the one the page lands. Plan order is not arrival
       * order once a press is reported again.
       */
      readonly answers: readonly ExtensionPushReportBody[];
      /**
       * Fix round 1 (P2) - recovered after a reload from what the tab still remembered: presses of
       * the day push recorded before the reload are not among `presses`, so the answer never speaks
       * for the whole day.
       */
      readonly recovered: boolean;
    }
  /** The page went away while waiting; what is not recorded yet is still remembered. */
  | { readonly kind: "stopped" };

/** A day push that ended with an answer for each of its presses. */
export type TmsDayPushDone = Extract<TmsDayPushEnding, { kind: "done" }>;

export interface TmsDayPushFlowDeps extends Omit<TmsPushFlowDeps, "prepare" | "memory"> {
  readonly prepareDay: (body: {
    day: string;
    presses: readonly { competitionId: TmsCompetitionId; pressId: string }[];
  }) => Promise<ExtensionPushDayPlanBody>;
  /** Where the tab remembers the day push's presses. Each press is forgotten on its own. */
  readonly dayMemory: TmsDayPushMemory;
  /** Each press's ending as it comes, while the rest of the day push goes on. */
  readonly onPressEnded?: (press: RememberedPress, ending: TmsPushPressEnding) => void;
}

/**
 * A press's ending, told to the page as it comes. The page's listener failing is the page's own
 * matter: it must not stop the presses after this one being reported, released or asked about.
 */
function tellPressEnded(deps: TmsDayPushFlowDeps, press: RememberedPress, ending: TmsPushPressEnding): void {
  try {
    deps.onPressEnded?.(press, ending);
  } catch {
    /* Told or not, the ending is kept, and the day push goes on. */
  }
}

/** One press's dependencies, out of the day push's: the per-press functions, with that press's memory. */
function pressDepsOf(deps: TmsDayPushFlowDeps, pressId: string): TmsPushPressDeps {
  const { prepareDay: _prepareDay, dayMemory, onPressEnded: _onPressEnded, ...rest } = deps;
  return { ...rest, memory: dayPressMemory(dayMemory, pressId) };
}

/** The answer an ending carries - the day as that report or release left it - or null. */
function answerOf(ending: TmsPushPressEnding): ExtensionPushReportBody | null {
  return ending.kind === "reported" || ending.kind === "released" ? ending.answer : null;
}

/**
 * Fix round 1 (P10) - ONE press of a day push, handled, whatever happens: the per-press function's
 * own ending, or - should the page itself fail while handling it - no result, `failed`, with what the
 * API already holds for it. The press stays remembered wherever that failure left it, so the next
 * load asks about it again; the presses after it are still handled, and the question always gets an
 * answer rather than staying on its last step.
 */
async function handledPress(
  deps: TmsDayPushFlowDeps,
  press: RememberedPress,
  run: (pressDeps: TmsPushPressDeps) => Promise<TmsPushEnding>,
): Promise<TmsPushEnding> {
  const pressDeps = pressDepsOf(deps, press.pressId);
  try {
    return await run(pressDeps);
  } catch {
    return { kind: "no-result", press, why: "failed", recorded: await recordedFor(pressDeps, press) };
  }
}

const LEFT_OUT_REASONS: readonly string[] = ["nothing_to_push", "no_match_on_day", "not_in_this_push"];

/**
 * Why the day's answer cannot be handed on, or null when it is the day push the page asked for: its
 * day; at least one plan, each for a press id the page minted, paired with that press's competition,
 * and each a plan the single press would hand on ({@link planFault}); the plans in the send order
 * (ascending competition id); the left-out competitions ones the page asked for, for a reason it
 * knows; and every competition asked for exactly once, planned or left out.
 */
function dayPlanFault(answer: ExtensionPushDayPlanBody, presses: readonly RememberedPress[]): string | null {
  if (!isRecord(answer)) return "the server's answer is not a day push's plans";
  if (answer.day !== presses[0]?.day) return "the plans are for another day";
  if (!Array.isArray(answer.plans) || answer.plans.length === 0) return "the answer holds no plan";
  if (!Array.isArray(answer.leftOut)) return "the answer does not say which competitions were left out";
  const accounted = new Set<string>();
  let previous = "";
  for (const plan of answer.plans as unknown[]) {
    if (!isRecord(plan)) return "a plan is not one";
    const pressId = plan["pressId"];
    const press = presses.find((candidate) => typeof pressId === "string" && candidate.pressId === pressId.toLowerCase());
    if (press === undefined) return "a plan is for another push";
    const fault = planFault(plan as unknown as ExtensionPushPlanBody, press);
    if (fault !== null) return fault;
    if (accounted.has(press.competitionId)) return "two plans are for one competition";
    if (press.competitionId <= previous) return "the plans are not in the send order";
    previous = press.competitionId;
    accounted.add(press.competitionId);
  }
  for (const entry of answer.leftOut as unknown[]) {
    if (!isRecord(entry)) return "a competition left out is not named";
    const competitionId = entry["competitionId"];
    if (!presses.some((press) => press.competitionId === competitionId)) {
      return "a competition left out is not one this push asked for";
    }
    if (typeof entry["reason"] !== "string" || !LEFT_OUT_REASONS.includes(entry["reason"])) {
      return "a competition is left out for a reason this page does not know";
    }
    if (accounted.has(String(competitionId))) return "a competition is both planned and left out";
    accounted.add(String(competitionId));
  }
  if (accounted.size !== presses.length) return "a competition this push asked for is neither planned nor left out";
  return null;
}

/**
 * PREPARE-DAY - asked once, and once more with the SAME press ids when its answer was lost
 * ({@link isLostPrepareAnswer}), exactly as the single prepare is: the server answers press ids it
 * already holds, while their presses are open, with the plans it recorded, so the second ask never
 * prepares a second day push.
 */
async function prepareDayPush(
  deps: TmsDayPushFlowDeps,
  day: string,
  presses: readonly RememberedPress[],
): Promise<ExtensionPushDayPlanBody> {
  const body = {
    day,
    presses: presses.map((press) => ({ competitionId: press.competitionId, pressId: press.pressId })),
  };
  try {
    return await deps.prepareDay(body);
  } catch (error) {
    if (!isLostPrepareAnswer(error)) throw error;
    await deps.sleep(deps.timing.reportRetryMs);
    if (deps.gone()) throw error;
    return deps.prepareDay(body);
  }
}

/**
 * The day push, from `Confirm Push`: discover, prepare every competition, hand the plans over in ONE
 * `push-day` message, and report each result as it arrives - or release, or ask - press by press, in
 * the send order. Resolves with how it ended; never throws.
 *
 * **Reports go one at a time, in plan order**, each through `reportTmsPush` - the unchanged report,
 * resumable, and `ack` after its genuine answer only - while the port stays open for the next press's
 * result. After the port has ended, every press still without a result is:
 *
 *   * RELEASED - one at a time, through `releaseTmsPush` - only where the day push's message certainly
 *     never reached the extension: its `refused` of busy or invalid-message as its first word, or the
 *     message never sent (no extension after all, this page's own push still out, or over 1 MiB);
 *   * otherwise ASKED ABOUT, through `recoverTmsPush` - a lost port, an `internal` or
 *     `already-finished` refusal, a message the page could not read, or the page's own wait ending at
 *     the hold: the extension may have run it, and only its own `status` can say. A press still
 *     waiting its turn answers `pending`, and is waited for as a running one is.
 *
 * The hold is each press's DURATION on this device's steady clock, exactly as the single press's; the
 * page stops waiting on the port at the latest press's deadline and margin.
 */
export async function runTmsDayPush(
  deps: TmsDayPushFlowDeps,
  target: { readonly day: string; readonly competitionIds: readonly TmsCompetitionId[] },
): Promise<TmsDayPushEnding> {
  deps.onStep("discovering", null);
  let present: boolean;
  try {
    // Three seconds at the press, as the single press asks: a worker Chrome let sleep takes a moment.
    present = (await deps.bridge.hello(TMS_PUSH_PRESS_HELLO_TIMEOUT_MS)).kind === "present";
  } catch {
    present = false;
  }
  const pushDay = deps.bridge.pushDay;
  // A bridge that cannot carry a day push is no extension to push a day through: nothing prepared.
  if (!present || pushDay === undefined) return { kind: "absent" };

  // Men's before Women's: ascending competition id is the send order.
  const competitionIds = [...new Set(target.competitionIds)].sort();
  if (competitionIds.length === 0) {
    return { kind: "refused", error: new TmsPushPlanMismatchError("the day has no competition to push") };
  }
  let presses: RememberedPress[] = competitionIds.map((competitionId) => ({
    pressId: deps.mintPressId().toLowerCase(),
    day: target.day,
    competitionId,
    holdUntil: null,
    holdDeadline: null,
    planned: null,
  }));
  // Remembered before the prepare is asked: a reload while it is out still knows every press id.
  deps.dayMemory.write(presses);
  deps.onStep("preparing", null);

  let answer: ExtensionPushDayPlanBody;
  try {
    answer = await prepareDayPush(deps, target.day, presses);
  } catch (error) {
    deps.dayMemory.clear();
    return { kind: "refused", error };
  }
  // Each hold starts counting down on this device the moment its remaining time arrived.
  const arrived = deps.clock();
  const fault = dayPlanFault(answer, presses);
  if (fault !== null) {
    deps.dayMemory.clear();
    return { kind: "refused", error: new TmsPushPlanMismatchError(fault) };
  }

  // The presses the server kept, in its send order - a competition left out was recorded nowhere, and
  // is forgotten here.
  const minted = presses;
  presses = answer.plans.map((plan) => {
    const press = minted.find((candidate) => candidate.pressId === plan.pressId.toLowerCase()) as RememberedPress;
    return {
      ...press,
      holdUntil: plan.holdUntil,
      holdDeadline: arrived + plan.holdRemainingMs,
      planned: plan.payloads.map((payload) => ({
        tmsMatchDbId: payload.tmsMatchDbId,
        displayMatchNo: payload.displayMatchNo,
      })),
    };
  });
  deps.dayMemory.write(presses);
  // Fix round 1 (P9): a page that has gone while the prepare was answered hands nothing over -
  // nobody is left to follow the day push - and its plans certainly never left it, which is 6b's
  // ground for a release: every press is released, one at a time, and the day freed now.
  if (deps.gone()) {
    for (const press of presses) {
      await handledPress(deps, press, (pressDeps) =>
        releaseTmsPush(pressDeps, press, { by: "page", reason: "gone" }),
      );
    }
    return { kind: "stopped" };
  }
  deps.onStep("handing-over", presses[0] ?? null);

  // The plans as the server answered them, each with what is left of ITS hold now.
  const handed: ExtensionPushPlanBody[] = answer.plans.map((plan) => ({
    ...plan,
    holdRemainingMs: Math.max(0, plan.holdRemainingMs - Math.round(deps.clock() - arrived)),
  }));

  // One press at a time, in plan order: a result's report, a release or a question, chained. Each
  // answer the API gives is kept in the order it arrived (P1), and a press the page fails to handle
  // is no result rather than a chain that stops (P10).
  const endings = new Map<string, TmsPushPressEnding>();
  const answers: ExtensionPushReportBody[] = [];
  let stopped = false;
  let chain: Promise<void> = Promise.resolve();
  const settle = (press: RememberedPress, run: (pressDeps: TmsPushPressDeps) => Promise<TmsPushEnding>): void => {
    chain = chain
      .then(async () => {
        // The page has gone: what is not recorded yet stays remembered, for the next load.
        if (stopped) return;
        const ending = await handledPress(deps, press, run);
        if (ending.kind === "stopped") {
          stopped = true;
          return;
        }
        if (!isPressEnding(ending)) return;
        endings.set(press.pressId, ending);
        const answered = answerOf(ending);
        if (answered !== null) answers.push(answered);
        tellPressEnded(deps, press, ending);
      })
      // Nothing above throws; kept so that no press after this one could ever be left unhandled.
      .catch(() => undefined);
  };
  const received = new Set<string>();

  // The page waits on the port no longer than the latest hold, plus the margin - on this device's
  // steady clock. Past it the extension posts nothing, and the page asks what it kept.
  const stop = new AbortController();
  const lastDeadline = Math.max(...presses.map((press) => press.holdDeadline ?? arrived));
  const timer = setTimeout(
    () => {
      stop.abort();
    },
    Math.max(0, lastDeadline + deps.timing.graceMs - deps.clock()),
  );
  let ending: TmsPushDayPortEnding;
  try {
    ending = await pushDay.call(
      deps.bridge,
      target.day,
      handed,
      {
        progress: (stage, pressId) => {
          const press = presses.find((candidate) => candidate.pressId === pressId);
          if (press !== undefined) deps.onStep(stage, press);
        },
        result: (index, result) => {
          const press = presses[index];
          if (press === undefined) return;
          received.add(press.pressId);
          // Reported EXACTLY as the extension sent it, as it arrives - after the ones before it.
          settle(press, (pressDeps) => reportTmsPush(pressDeps, press, result));
        },
      },
      stop.signal,
    );
  } catch {
    // The bridge answers every ending rather than throwing; one that threw is read as a lost port.
    ending = { kind: "lost" };
  } finally {
    clearTimeout(timer);
  }

  const waiting = presses.filter((press) => !received.has(press.pressId));
  const release = (notTaken: TmsPushNotTaken): void => {
    for (const press of waiting) settle(press, (pressDeps) => releaseTmsPush(pressDeps, press, notTaken));
  };
  const ask = (): void => {
    for (const press of waiting) settle(press, (pressDeps) => recoverTmsPush(pressDeps, press));
  };
  switch (ending.kind) {
    case "done":
      break;
    case "refused":
      // `internal`: it failed while taking the day push, and may hold it. `already-finished`: it
      // knows a press of it as run. Either way: ask, never release.
      if (ending.reason === "internal" || ending.reason === "already-finished") ask();
      else release({ by: "extension", reason: ending.reason, detail: ending.detail });
      break;
    case "busy":
      release({ by: "page", reason: "busy" });
      break;
    case "unavailable":
      release({ by: "page", reason: "absent" });
      break;
    case "too-large":
      release({ by: "page", reason: "too-large" });
      break;
    case "lost":
    case "stopped":
    case "unreadable":
      ask();
      break;
  }
  await chain;
  if (stopped) return { kind: "stopped" };

  return {
    kind: "done",
    day: target.day,
    presses: presses.flatMap((press) => {
      const pressEnding = endings.get(press.pressId);
      return pressEnding === undefined ? [] : [{ press, ending: pressEnding }];
    }),
    leftOut: answer.leftOut,
    answers,
    recovered: false,
  };
}

/**
 * RECOVER a day push this tab remembered from before a reload (section 6c: "a reload recovers each one
 * as 6b recovers one"): each remembered press in the send order, through `recoverTmsPush` - `status`,
 * a kept result reported, and before "no result" the day's receipts asked for.
 *
 * Fix round 1 (P2): the ending is `recovered` - the tab remembers only the presses it had not yet seen
 * recorded, so a competition of the day push reported before the reload is not among them, and the
 * answer never speaks for the whole day.
 */
export async function recoverTmsDayPush(
  deps: TmsDayPushFlowDeps,
  presses: readonly RememberedPress[],
): Promise<TmsDayPushEnding> {
  const ended: TmsDayPushPress[] = [];
  const answers: ExtensionPushReportBody[] = [];
  for (const press of presses) {
    const ending = await handledPress(deps, press, (pressDeps) => recoverTmsPush(pressDeps, press));
    if (ending.kind === "stopped") return { kind: "stopped" };
    if (!isPressEnding(ending)) continue;
    ended.push({ press, ending });
    const answered = answerOf(ending);
    if (answered !== null) answers.push(answered);
    tellPressEnded(deps, press, ending);
  }
  return {
    kind: "done",
    day: presses[0]?.day ?? "",
    presses: ended,
    leftOut: [],
    answers,
    recovered: true,
  };
}

/**
 * `Report Again` on a day push's answer: each press whose result was kept and not recorded, in the
 * send order, sent again UNCHANGED through `reportTmsPush`; every other press keeps its ending. The
 * answers go on in the order they arrive after the ones before (fix round 1, P1): the day as it now
 * stands is the NEWEST answer - a press reported again carries it, not the press after it in plan
 * order, whose answer is older.
 */
export async function reportTmsDayPushAgain(
  deps: TmsDayPushFlowDeps,
  previous: TmsDayPushDone,
): Promise<TmsDayPushEnding> {
  const ended: TmsDayPushPress[] = [];
  const answers = [...previous.answers];
  for (const entry of previous.presses) {
    if (entry.ending.kind !== "report-refused" || !entry.ending.retryable) {
      ended.push(entry);
      continue;
    }
    const result = entry.ending.result;
    const ending = await handledPress(deps, entry.press, (pressDeps) => reportTmsPush(pressDeps, entry.press, result));
    if (ending.kind === "stopped") return { kind: "stopped" };
    if (!isPressEnding(ending)) {
      ended.push(entry);
      continue;
    }
    ended.push({ press: entry.press, ending });
    const answered = answerOf(ending);
    if (answered !== null) answers.push(answered);
    tellPressEnded(deps, entry.press, ending);
  }
  return {
    kind: "done",
    day: previous.day,
    presses: ended,
    leftOut: previous.leftOut,
    answers,
    recovered: previous.recovered,
  };
}
